import type {
  RatingCriteriaDetails,
  RatingCriteriaOption,
} from '@features/buyer/jobs/criteria-option';

export const RATING_SCALE_MAX = 8;
const FAIR_SCORE_MIN = 5;

export interface RatedCriteria extends RatingCriteriaDetails {
  id: string | number;
  title?: string;
  options: RatingCriteriaOption[];
  includeCommonImages?: boolean;
  includeCriteriaNotes?: boolean;
}

export type CriteriaResult =
  | { kind: 'score'; score: number }
  | { kind: 'yesNo'; passed: boolean }
  | { kind: 'choice'; label: string }
  | { kind: 'recorded' }
  | { kind: 'notRated' };

export type ResultTone = 'good' | 'fair' | 'poor' | 'neutral' | 'muted';

export interface RatingSummary {
  total: number;
  checked: number;
  issues: number;
  averageScore: number | null;
}

const isAnswered = (option: RatingCriteriaOption): boolean =>
  option.ratingCriteriaUser != null;

export const criteriaResult = (
  options: RatingCriteriaOption[]
): CriteriaResult => {
  const picked = options.find(
    (option) => option.type === 'Option' && isAnswered(option)
  );
  if (!picked) {
    return options.some(isAnswered)
      ? { kind: 'recorded' }
      : { kind: 'notRated' };
  }

  const raw = String(picked.ratingValue ?? '').trim();
  if (raw === '') return { kind: 'recorded' };

  const score = Number(raw);
  if (Number.isFinite(score)) return { kind: 'score', score };

  const normalized = raw.toLowerCase();
  if (normalized === 'yes' || normalized === 'no') {
    return { kind: 'yesNo', passed: normalized === 'yes' };
  }
  return { kind: 'choice', label: raw };
};

export const resultTone = (result: CriteriaResult): ResultTone => {
  switch (result.kind) {
    case 'score':
      if (result.score >= RATING_SCALE_MAX) return 'good';
      return result.score >= FAIR_SCORE_MIN ? 'fair' : 'poor';
    case 'yesNo':
      return result.passed ? 'good' : 'poor';
    case 'choice':
    case 'recorded':
      return 'neutral';
    case 'notRated':
      return 'muted';
    default: {
      const exhaustive: never = result;
      return exhaustive;
    }
  }
};

export const isIssue = (result: CriteriaResult): boolean =>
  (result.kind === 'score' && result.score < RATING_SCALE_MAX) ||
  (result.kind === 'yesNo' && !result.passed);

export const summarizeRatings = (results: CriteriaResult[]): RatingSummary => {
  const scores = results.flatMap((result) =>
    result.kind === 'score' ? [result.score] : []
  );
  return {
    total: results.length,
    checked: results.filter((result) => result.kind !== 'notRated').length,
    issues: results.filter(isIssue).length,
    averageScore: scores.length
      ? scores.reduce((sum, score) => sum + score, 0) / scores.length
      : null,
  };
};
